import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ANNOUNCER_PRESETS } from "../components/DirectorMixerRack";
import useMidiController from "../lib/useMidiController";
import SeratoDeck from "../components/dj/SeratoDeck";
import WaveformStrip from "../components/dj/WaveformStrip";
import TrackBrowser from "../components/dj/TrackBrowser";
import MidiPanel from "../components/dj/MidiPanel";
import FxPanel from "../components/dj/FxPanel";
import AdQueuePanel from "../components/dj/AdQueuePanel";
import CallInPanel from "../components/dj/CallInPanel";
import EpisodesPanel from "../components/dj/EpisodesPanel";
import BroadcastPanel from "../components/dj/BroadcastPanel";
import MicInputSelector from "../components/dj/MicInputSelector";
import MicRoutingPopover from "../components/dj/MicRoutingPopover";
import SpotifyPanel from "../components/dj/SpotifyPanel";
import useSpotifyAuth from "../lib/useSpotifyAuth";
import useSpotifyPlayer from "../lib/useSpotifyPlayer";
import SetlistPanel from "../components/dj/SetlistPanel";
import VoiceDropsPanel from "../components/dj/VoiceDropsPanel";
import RequestsPanel from "../components/dj/RequestsPanel";
import SponsorDropsPanel from "../components/dj/SponsorDropsPanel";
import RecurringTemplatesPanel from "../components/dj/RecurringTemplatesPanel";
import RestreamPanel from "../components/dj/RestreamPanel";
import AnnouncementsPanel from "../components/dj/AnnouncementsPanel";
import HostPlaylistsPanel from "../components/dj/HostPlaylistsPanel";
import HostAdSalesPanel from "../components/dj/HostAdSalesPanel";
import RadioShowHistoryModal from "../components/RadioShowHistoryModal";
import RadioHostToolbar from "../components/dj/RadioHostToolbar";
import useDjMixerEngine from "../lib/useDjMixerEngine";
import useBpmAnalyzer from "../lib/useBpmAnalyzer";
import useSetRecorder from "../lib/useSetRecorder";
import useRadioCallinHost from "../lib/useRadioCallinHost";
import useCanvasBroadcast from "../lib/useCanvasBroadcast";
import useAutoMixSweep from "../lib/useAutoMixSweep";
import useSetlistLock from "../lib/useSetlistLock";

/**
 * RadioHostStudio — Serato-inspired DJ studio for Tap IN Radio hosts.
 *
 * All side panels live under /components/dj/ — this file now only wires the
 * top-level state (decks, xfader, mic, engine, recorder, call-in, midi) and
 * lays out the toolbar + deck row + master bar + drawers + browser.
 */
export default function RadioHostStudio() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [station, setStation] = useState(null);
  const [session, setSession] = useState(null);
  const [live, setLive] = useState(false);
  // Post-show analytics modal — opens automatically on End Show, and is
  // also reachable any time via the toolbar "History" button.
  const [historyOpen, setHistoryOpen] = useState(false);

  const [deckA, setDeckA] = useState(null);
  const [deckB, setDeckB] = useState(null);
  const [playA, setPlayA] = useState(false);
  const [playB, setPlayB] = useState(false);
  const [xfader, setXfader] = useState(0.5);
  const [masterVol, setMasterVol] = useState(1.0);
  const [musicVol, setMusicVol] = useState(0.8);
  const [onMic, setOnMic] = useState(false);
  // Mic Music Mode — when true the mic input acts as a clean line-in (no
  // echo-cancel / noise-suppress / AGC) so the DJ can plug a phone or audio
  // interface in and use the mic jack as a music source. Persisted so a
  // host returning to the studio keeps their setup.
  const [micMusicMode, setMicMusicMode] = useState(() => {
    try { return localStorage.getItem("tapin_dj_mic_music_mode") === "1"; } catch { return false; }
  });
  // Mic gain (0..1) — the actual GainNode is driven by `gain * 2` in the
  // engine so 0.5 ≈ unity, 1.0 ≈ +6dB push for hot mics.
  const [micGain, setMicGain] = useState(() => {
    try { const v = parseFloat(localStorage.getItem("tapin_dj_mic_gain") || ""); return Number.isFinite(v) ? v : 0.55; } catch { return 0.55; }
  });
  // Mic FX-send — defaults off so talk stays clean. Hosts flip this on when
  // music is coming through the mic input + they want it shaped by the FX bus.
  const [micFxSend, setMicFxSend] = useState(false);
  // Auto-record toggle — persists on the user (server-side) so a host's
  // preference survives across studio sessions. Local mirror so the UI
  // doesn't blink while the network round-trip resolves.
  const [autoRecord, setAutoRecord] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dj/auto-record");
        if (!cancelled) setAutoRecord(!!data?.enabled);
      } catch { /* ignore — default false */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const toggleAutoRecord = async () => {
    const next = !autoRecord;
    setAutoRecord(next);
    try {
      await api.post("/dj/auto-record", { enabled: next });
      toast.success(next ? "Auto-record ON — every show is saved." : "Auto-record OFF.");
    } catch (e) {
      setAutoRecord(!next);
      toast.error(formatApiError ? formatApiError(e) : "Couldn't save auto-record preference");
    }
  };
  // Smart-mix beat-align — when ON, the auto-mix sweep duration is
  // rounded to the nearest multiple of 8 beats of the playing deck's
  // BPM so transitions land on a bar boundary instead of mid-measure.
  // Purely additive: the engine math is untouched; we only adjust the
  // sweep tick's `dur`.
  const [smartMix, setSmartMix] = useState(() => {
    try { return localStorage.getItem("tapin_dj_smart_mix") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("tapin_dj_smart_mix", smartMix ? "1" : "0"); } catch { /* ignore */ }
  }, [smartMix]);
  const [fx, setFx] = useState("off");
  const [cueA, setCueA] = useState(false);
  const [cueB, setCueB] = useState(false);
  const [fxType, setFxType] = useState("off");
  const [fxAmount, setFxAmount] = useState(0);
  // Remembered audio-input device id (external mixer / interface / bluetooth mic)
  const [micDeviceId, setMicDeviceId] = useState(() => {
    try { return localStorage.getItem("tapin_dj_mic_device") || ""; } catch { return ""; }
  });

  const audioA = useRef(null);
  const audioB = useRef(null);
  const dropAudioRef = useRef(null);
  const [dropPlaying, setDropPlaying] = useState(null);

  // Per-deck channel state reported up from SeratoDeck
  const [chanA, setChanA] = useState({ gain: 0.5, eqLow: 0.5, eqMid: 0.5, eqHi: 0.5, tempo: 0.5 });
  const [chanB, setChanB] = useState({ gain: 0.5, eqLow: 0.5, eqMid: 0.5, eqHi: 0.5, tempo: 0.5 });
  const handleChannelChange = useCallback((sideKey, next) => {
    if (sideKey === "A") setChanA(next); else setChanB(next);
  }, []);

  // Scratch monitor — when the user grabs the platter, force-open that deck's
  // channel via the engine so the rub is audible regardless of crossfader
  // position. Without this, casual users would touch the platter and hear
  // nothing because chan.gain is 0 unless the deck is mixed in.
  const [scratchA, setScratchA] = useState(false);
  const [scratchB, setScratchB] = useState(false);
  const handleScratchingChange = useCallback((sideKey, isScratching) => {
    if (sideKey === "A") setScratchA(!!isScratching);
    else setScratchB(!!isScratching);
  }, []);

  // Per-deck FX routing — when both true (default) the FX bus mixes both
  // decks (legacy behaviour); when one is false, that deck plays clean
  // direct to master and FX only shapes the other deck. Toggled via the
  // small "FX" pill above each deck.
  const [fxSendA, setFxSendA] = useState(true);
  const [fxSendB, setFxSendB] = useState(true);

  // Scratch monitor — how loud the scratch rub bumps over the mix when the
  // host grabs the platter. 0.45 by default is "noticeable but musical";
  // battle DJs can crank to 1.0, lounge DJs can dial back to 0.2.
  const [scratchBoost, setScratchBoost] = useState(0.45);

  const engine = useDjMixerEngine({
    audioARef: audioA,
    audioBRef: audioB,
    trackAId: deckA?.id || null,
    trackBId: deckB?.id || null,
    eqA: { low: chanA.eqLow, mid: chanA.eqMid, hi: chanA.eqHi, gain: chanA.gain },
    eqB: { low: chanB.eqLow, mid: chanB.eqMid, hi: chanB.eqHi, gain: chanB.gain },
    xfader, musicVol, masterVol,
    micEnabled: onMic,
    micDeviceId,
    micMusicMode,
    micGain,
    micFxSend,
    cueA, cueB,
    scratchA, scratchB,
    scratchBoost,
    fxSendA, fxSendB,
    fxType, fxAmount,
  });

  const bpmA = useBpmAnalyzer(deckA?.source_url || null, !!deckA);
  const bpmB = useBpmAnalyzer(deckB?.source_url || null, !!deckB);

  // BPM analyzer runs against the actual decoded audio buffer — accurate
  // but takes 3-5s per track. Use the seeded `track.bpm` (inferred from the
  // music library row OR backend metadata) as an INSTANT fallback so SYNC
  // works the moment a track is loaded. The analyzer's value supersedes
  // it once available.
  const effectiveBpmA = bpmA.bpm || (deckA?.bpm > 0 ? deckA.bpm : null);
  const effectiveBpmB = bpmB.bpm || (deckB?.bpm > 0 ? deckB.bpm : null);

  const recorder = useSetRecorder(() => engine.getBroadcastStream(), { label: `tapin-radio-${slug}` });

  // Voice-drop playback — plays a pre-recorded host drop through an
  // independent <audio> that bypasses the deck channels and routes
  // straight to the master bus, so the drop sits on top of any music
  // currently playing without touching the crossfader. Stopping a drop
  // resets state; only one drop plays at a time.
  const playDrop = (drop) => {
    if (!drop?.data_uri) return;
    const a = dropAudioRef.current;
    if (!a) return;
    try {
      a.src = drop.data_uri;
      a.currentTime = 0;
      a.play().then(() => setDropPlaying(drop.id)).catch(() => {});
    } catch { /* ignore */ }
  };
  // The hidden audio element triggers onEnded → reset state
  const onDropEnded = () => setDropPlaying(null);

  // ─── Spotify "Send to Deck" wiring ────────────────────────────────
  // Lifted to studio level so the SpotifyPanel + each SeratoDeck share
  // ONE auth and ONE Web Playback SDK instance (Spotify rejects duplicate
  // device-names anyway). When a host clicks → A / → B on a Spotify
  // search row, we stamp a Spotify-flagged track shape onto the deck so
  // the visualiser + UI work, but the audio is driven by the SDK
  // (`spotifyPlayer.play(uri)`) instead of the HTMLAudioElement, since
  // DRM forbids us from tapping it through Web Audio.
  //
  // Listeners on the in-app player hear silence on Spotify decks (per
  // user choice option B). The OBS / Cloudflare RTMP feed picks up the
  // Spotify audio if the host routes system audio into OBS.
  const spotifyAuth = useSpotifyAuth();
  const spotifyPlayer = useSpotifyPlayer({
    enabled: spotifyAuth.loggedIn,
    getFreshAccessToken: spotifyAuth.getFreshAccessToken,
  });
  // Build the deck-shaped track from a Spotify search result so the
  // studio's existing deck-rendering paths (cover, title, artist, BPM)
  // light up without bespoke branches.
  const sendSpotifyToDeck = (side, sp) => {
    if (!sp?.uri) return;
    const cover = sp.album?.images?.[0]?.url || sp.album?.images?.[1]?.url || "";
    const artist = (sp.artists || []).map((a) => a.name).join(", ");
    const t = {
      id: `spotify:${sp.id}`,
      title: sp.name,
      artist,
      album: sp.album?.name || "",
      cover_url: cover,
      duration_sec: sp.duration_ms ? Math.round(sp.duration_ms / 1000) : 0,
      source_type: "spotify",
      spotify_uri: sp.uri,
      spotify_id: sp.id,
      _spotify: true,
      // Disable HTML audio path — the deck's audioRef won't have a real
      // source; play/pause is routed through the Spotify SDK below.
      source_url: "",
      _unplayable: false,
    };
    if (side === "A") { setDeckA(t); setPlayA(false); }
    else { setDeckB(t); setPlayB(false); }
    if (!spotifyAuth.loggedIn) {
      toast.info("Sign in with Spotify (Premium) to play this track. Track pinned for now.");
    } else if (!spotifyPlayer.ready) {
      toast.info("Spotify player is connecting — give it a few seconds before hitting Play.");
    } else {
      toast.success(`→ Deck ${side}: ${sp.name} (Spotify · monitor only)`);
    }
  };

  // Drive the Spotify SDK from each deck's PLAY/PAUSE state when the
  // loaded track is a Spotify track. Each effect runs only when its
  // deck's Spotify track id OR play state changes. The HTML audio
  // element useEffect below skips Spotify tracks (its src will be empty).
  useEffect(() => {
    if (!deckA?._spotify || !spotifyPlayer.ready) return;
    if (playA) { spotifyPlayer.play(deckA.spotify_uri); }
    else { spotifyPlayer.pause(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playA, deckA?.spotify_uri, spotifyPlayer.ready]);
  useEffect(() => {
    if (!deckB?._spotify || !spotifyPlayer.ready) return;
    if (playB) { spotifyPlayer.play(deckB.spotify_uri); }
    else { spotifyPlayer.pause(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playB, deckB?.spotify_uri, spotifyPlayer.ready]);

  // Volume routing: master * music * (channel gain on the Spotify deck)
  // * (crossfader contribution). Equal-power approximation matches the
  // existing engine's voicing so the perceived crossfade between a
  // library deck and a Spotify deck stays roughly balanced.
  useEffect(() => {
    if (!spotifyPlayer.ready) return;
    const aActive = !!deckA?._spotify;
    const bActive = !!deckB?._spotify;
    if (!aActive && !bActive) return;
    const xfA = Math.cos((Math.PI / 2) * xfader);   // 1 at xfader=0, 0 at 1
    const xfB = Math.sin((Math.PI / 2) * xfader);   // 0 at xfader=0, 1 at 1
    const gA = aActive ? (chanA.gain * xfA) : 0;
    const gB = bActive ? (chanB.gain * xfB) : 0;
    const v = Math.max(0, Math.min(1, masterVol * musicVol * Math.max(gA, gB)));
    spotifyPlayer.setVolume(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xfader, chanA.gain, chanB.gain, masterVol, musicVol, deckA?._spotify, deckB?._spotify, spotifyPlayer.ready]);


  // Canvas-backed video track (track art + deck info + LIVE timer) composed
  // with the engine's audio — MediaRecorder-safe for MBaaS destinations like
  // YouTube/Facebook/Twitch which require a video track.
  const engineAudioStreamRef = useRef(null);
  useEffect(() => { engineAudioStreamRef.current = engine.getBroadcastStream(); });
  const canvasBroadcast = useCanvasBroadcast({
    audioStreamRef: engineAudioStreamRef,
    meta: {
      stationName: station?.name, hostName: user?.display_name || user?.username,
      deckA, deckB, xfader,
    },
  });

  // Tracklist — appended whenever a track is loaded onto either deck during a recording
  const tracklistRef = useRef([]);
  useEffect(() => {
    if (recorder.recording && deckA?.id) {
      tracklistRef.current.push({
        at_sec: recorder.durationSec,
        title: deckA.title, artist: deckA.artist, deck: "A", cover_url: deckA.cover_url,
      });
    }
    // eslint-disable-next-line
  }, [deckA?.id]);
  useEffect(() => {
    if (recorder.recording && deckB?.id) {
      tracklistRef.current.push({
        at_sec: recorder.durationSec,
        title: deckB.title, artist: deckB.artist, deck: "B", cover_url: deckB.cover_url,
      });
    }
    // eslint-disable-next-line
  }, [deckB?.id]);
  useEffect(() => {
    if (recorder.recording && tracklistRef.current.length === 0) {
      tracklistRef.current = [];
      if (deckA?.id) tracklistRef.current.push({ at_sec: 0, title: deckA.title, artist: deckA.artist, deck: "A", cover_url: deckA.cover_url });
      if (deckB?.id) tracklistRef.current.push({ at_sec: 0, title: deckB.title, artist: deckB.artist, deck: "B", cover_url: deckB.cover_url });
    }
    // eslint-disable-next-line
  }, [recorder.recording]);

  // Call-in state
  const [callinToken, setCallinToken] = useState(null);
  const callin = useRadioCallinHost({
    slug,
    enabled: !!callinToken,
    token: callinToken,
    onCallerAudio: (callerId, stream) => {
      engine.attachStream?.(`caller:${callerId}`, stream, { gainValue: 1.2 });
    },
  });
  const openCallin = async () => {
    try {
      const { data } = await api.post("/radio/host/callin/open", { station_slug: slug });
      if (data?.token) setCallinToken(data.token);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const closeCallin = async () => {
    try {
      await api.post("/radio/host/callin/close", { station_slug: slug });
    } catch {}
    Object.keys(callin.callers).forEach((cid) => engine.detachStream?.(`caller:${cid}`));
    setCallinToken(null);
  };

  // MIDI
  const midi = useMidiController({ storageKey: `tapin_dj_midi:${slug}` });
  useEffect(() => {
    midi.bindHandler("deck-a-play", (v) => { if (v === 1) setPlayA((p) => !p); });
    midi.bindHandler("deck-b-play", (v) => { if (v === 1) setPlayB((p) => !p); });
    midi.bindHandler("deck-a-cue",  (v) => { if (v === 1 && audioA.current) audioA.current.currentTime = 0; });
    midi.bindHandler("deck-b-cue",  (v) => { if (v === 1 && audioB.current) audioB.current.currentTime = 0; });
    midi.bindHandler("mic-toggle",  (v) => { if (v === 1) setOnMic((m) => !m); });
    midi.bindHandler("xfader",      (v) => setXfader(v));
    midi.bindHandler("music-vol",   (v) => setMusicVol(v));
    midi.bindHandler("master-vol",  (v) => setMasterVol(v));
    return () => ["deck-a-play","deck-b-play","deck-a-cue","deck-b-cue","mic-toggle","xfader","music-vol","master-vol"]
      .forEach((id) => midi.unbindHandler(id));
    // eslint-disable-next-line
  }, [midi.enabled]);

  // Load station
  const [tracks, setTracks] = useState([]);
  const loadAll = useCallback(async () => {
    try {
      const { data: s } = await api.get(`/radio/stations/${slug}`);
      setStation(s);
      setTracks(s.tracks || []);
    } catch { setStation({ error: true }); }
    try {
      const { data } = await api.get(`/radio/host/session/${slug}`);
      setSession(data);
      setLive(!!data?.is_live && data?.host_id === user?.user_id);
    } catch {}
  }, [slug, user?.user_id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!audioA.current) return;
    // Spotify decks are driven by the SDK (see Spotify effect above).
    if (deckA?._spotify) return;
    playA ? audioA.current.play().catch(() => {}) : audioA.current.pause();
  }, [playA, deckA?.id, deckA?._spotify]);
  useEffect(() => {
    if (!audioB.current) return;
    if (deckB?._spotify) return;
    playB ? audioB.current.play().catch(() => {}) : audioB.current.pause();
  }, [playB, deckB?.id, deckB?._spotify]);

  // Persist mic device choice
  useEffect(() => {
    try { micDeviceId ? localStorage.setItem("tapin_dj_mic_device", micDeviceId) : localStorage.removeItem("tapin_dj_mic_device"); } catch {}
  }, [micDeviceId]);

  // Persist mic music-mode + gain so DJs keep their tuning across sessions.
  useEffect(() => { try { localStorage.setItem("tapin_dj_mic_music_mode", micMusicMode ? "1" : "0"); } catch {} }, [micMusicMode]);
  useEffect(() => { try { localStorage.setItem("tapin_dj_mic_gain", String(micGain)); } catch {} }, [micGain]);

  // Session state push (debounced)
  const pushTimer = useRef(null);
  useEffect(() => {
    if (!live) return;
    clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(() => {
      // For Spotify decks, surface the Spotify URI + meta so the listener
      // page can render the "Now playing on Spotify · Listen along" tile
      // even though the audio itself is DRM-walled.
      const deckPayload = (deck, playing) => {
        if (!deck) return null;
        const base = { track_id: deck.id, playing };
        if (deck._spotify) {
          return {
            ...base,
            spotify: {
              uri: deck.spotify_uri,
              id: deck.spotify_id,
              title: deck.title,
              artist: deck.artist,
              album: deck.album,
              cover_url: deck.cover_url,
            },
          };
        }
        return base;
      };
      api.post("/radio/host/session/state", {
        station_slug: slug,
        current_deck_a: deckPayload(deckA, playA),
        current_deck_b: deckPayload(deckB, playB),
        xfader, on_mic: onMic, announcer_fx: fx, master_vol: masterVol, music_vol: musicVol,
        // Surface the new mic-music-mode flag so listener UIs can show a
        // "Music IN" badge while the DJ is mixing in an external feed.
        mic_music_mode: onMic && micMusicMode,
      }).catch(() => {});
    }, 600);
  }, [live, deckA, deckB, playA, playB, xfader, onMic, fx, masterVol, musicVol, micMusicMode, slug]);

  const goLive = async () => {
    try {
      await api.post("/radio/host/session/start", { station_slug: slug });
      setLive(true); toast.success("You're live on air", { id: "radio-live" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const endShow = async () => {
    try {
      await api.post("/radio/host/session/stop", { station_slug: slug });
      setLive(false); setPlayA(false); setPlayB(false);
      toast.info("Show ended", { id: "radio-live" });
      // Surface the just-ended session's analytics so the host gets an
      // immediate post-show recap (callers, requests, tips).
      setHistoryOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Sweep-mix macro — automated deck-to-deck transition. The implementation
  // (filter-and-crossfade animation, smart-mix beat-align, cleanup) lives
  // in /lib/useAutoMixSweep.js so this file stays readable.
  const {
    sweeping,
    sweepFromRef,
    sweepCompletedAt,
    sweepToNext,
    cancelSweep,
  } = useAutoMixSweep({
    xfader, setXfader,
    deckA, deckB,
    playA, playB, setPlayA, setPlayB,
    setFxType, setFxAmount,
    smartMix,
  });

  // SetList Lock — see below. Its hook depends on `smartLoad` (declared
  // further down) so the invocation is deferred to right after
  // smartLoad exists in scope, avoiding a TDZ ReferenceError.

  // Auto-mix on track end — when armed, the active deck's `timeupdate` is
  // watched and we trigger sweepToNext() once the remaining time is less
  // than 8 bars (8 × 60 / BPM × 4 seconds, i.e. 32 beats). Persisted across
  // sessions because solo DJs typically leave it on while mixing.
  const [autoMix, setAutoMix] = useState(() => {
    try { return localStorage.getItem("tapin_dj_automix") === "1"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("tapin_dj_automix", autoMix ? "1" : "0"); } catch {} }, [autoMix]);
  // Latch so we only fire ONCE per track-end. Reset whenever the
  // currently-dominant deck changes (xfader crosses 0.5) or the playing
  // track on the dominant side changes.
  const autoMixFiredRef = useRef("");
  useEffect(() => { autoMixFiredRef.current = ""; }, [deckA?.id, deckB?.id, xfader < 0.5]);
  useEffect(() => {
    if (!autoMix || sweeping) return;
    // Pick the dominant deck — that's the one listeners are hearing the
    // most of, and the one we should mix OUT of when it ends.
    const aDom = xfader < 0.5;
    const audio = aDom ? audioA.current : audioB.current;
    const deck = aDom ? deckA : deckB;
    const bpm = aDom ? effectiveBpmA : effectiveBpmB;
    if (!audio || !deck) return;
    const id = setInterval(() => {
      try {
        if (audio.paused || !audio.duration || !isFinite(audio.duration)) return;
        const remain = audio.duration - audio.currentTime;
        // 8 bars at 4 beats/bar = 32 beats; convert via current BPM (with
        // a safe 120 fallback when BPM unknown).
        const beatSec = 60 / Math.max(40, Math.min(220, bpm || 120));
        const cueAhead = 8 * 4 * beatSec;
        const fingerprint = `${deck.id}|${aDom ? "A" : "B"}`;
        if (remain > 0 && remain <= cueAhead && autoMixFiredRef.current !== fingerprint) {
          autoMixFiredRef.current = fingerprint;
          // Only fire if the OTHER deck has a track loaded — otherwise
          // we'd sweep into silence.
          const peerLoaded = aDom ? !!deckB : !!deckA;
          if (peerLoaded) {
            sweepToNext();
            toast.info(`Auto-mix → Deck ${aDom ? "B" : "A"}`, { id: "automix", duration: 2200 });
          }
        }
      } catch { /* ignore */ }
    }, 200);
    return () => clearInterval(id);
  }, [autoMix, sweeping, deckA, deckB, xfader, effectiveBpmA, effectiveBpmB]);

  const canDirectPlay = (t) => {
    if (!t || !t.source_url) return false;
    const u = String(t.source_url).toLowerCase();
    if (/\.(mp3|m4a|aac|wav|flac|ogg)(\?|$)/.test(u)) return true;
    if (u.includes("itunes.apple.com") || u.includes("mzstatic.com") || u.includes("audio-ssl.itunes") || u.includes("/download/")) return true;
    if (u.endsWith(".m3u8") || u.includes(".m3u8?")) return true;
    if (t.source_type === "url") return true;
    return false;
  };

  const smartLoad = async (t, setDeck, setPlay) => {
    setPlay(false);
    // Royalty: fire-and-forget log of every loaded track. Backend stores
    // creator_id, station_slug, track meta + timestamp so the host can
    // export an ASCAP/BMI/SOCAN-ready CSV of everything they aired.
    api.post("/dj/track-play", {
      track: { id: t.id, title: t.title || t.name, artist: t.artist, album: t.album, isrc: t.isrc, duration_sec: t.duration_sec },
      station_slug: slug,
      deck: setDeck === setDeckA ? "A" : "B",
    }).catch(() => {});
    // Defensive BPM hydration — if the track row didn't already carry a
    // `bpm` field (e.g. loaded from a path other than the table click),
    // derive a stable inferred BPM from the track id so SYNC has an
    // immediate fallback before useBpmAnalyzer finishes.
    const ensureBpm = (track) => {
      if (track && typeof track.bpm === "number" && track.bpm > 0) return track;
      // Hash to 70..165 BPM range — same formula the TrackBrowser uses for
      // its inferred BPM column, so the deck display matches the table row.
      const seed = String(track?.id || track?.title || "?");
      let h = 0;
      for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
      return { ...track, bpm: 70 + (h % 96), _bpm_inferred: true };
    };
    if (canDirectPlay(t)) { setDeck(ensureBpm(t)); return; }
    // Spotify auto-fallback — when the track has no direct URL but the
    // host is signed into Spotify, search the Spotify catalog for the
    // best title+artist match and pin it on the deck as a Spotify track.
    // This rescues all the legacy library tracks that previously relied
    // on iTunes preview URLs (now retired).
    //
    // We try three search variants in sequence so quirky catalog rows
    // (extra "(Remastered)", featuring tags, missing artist, etc) still
    // resolve:
    //   1. `track:"<title>" artist:"<artist>"` — strictest, best ranking
    //   2. `<title> <artist>`                  — loose phrase match
    //   3. `<title>`                           — last-resort by title only
    if (spotifyAuth.loggedIn) {
      const titleClean = (t.title || t.name || "").replace(/\([^)]*\)/g, "").trim();
      const artistClean = (t.artist || "").replace(/\([^)]*\)/g, "").trim();
      const queries = [
        titleClean && artistClean ? `track:"${titleClean}" artist:"${artistClean}"` : "",
        [titleClean, artistClean].filter(Boolean).join(" "),
        titleClean,
      ].filter((q) => q && q.length >= 2);
      try {
        const tok = await spotifyAuth.getFreshAccessToken();
        if (tok) {
          for (const q of queries) {
            // eslint-disable-next-line no-await-in-loop
            const r = await fetch(
              `https://api.spotify.com/v1/search?type=track&limit=1&q=${encodeURIComponent(q)}`,
              { headers: { Authorization: `Bearer ${tok}` } },
            );
            if (!r.ok) continue;
            // eslint-disable-next-line no-await-in-loop
            const d = await r.json();
            const sp = d?.tracks?.items?.[0];
            if (sp?.uri) {
              const side = setDeck === setDeckA ? "A" : "B";
              sendSpotifyToDeck(side, sp);
              return;
            }
          }
        }
        // Console breadcrumb so the host can debug a stubborn track.
        console.warn("[smartLoad] Spotify auto-fallback found no match for", { title: t.title, artist: t.artist, queries });
      } catch (e) {
        console.warn("[smartLoad] Spotify auto-fallback errored", e);
      }
    }
    // Last-resort: stamp the deck with the unplayable track + nudge the
    // host toward signing in to Spotify (or pasting a direct URL).
    setDeck(ensureBpm({ ...t, _unplayable: true }));
    if (!spotifyAuth.loggedIn) {
      toast.error("Sign in to Spotify (Premium) to play this track — or paste a direct MP3 URL via the library editor.", { id: "deck-unplayable", duration: 6000 });
    } else {
      // Build a Spotify search URL so the host can manually inspect
      // why the catalog didn't match (typo, instrumental cover, etc.)
      const manual = encodeURIComponent(`${t.title || ""} ${t.artist || ""}`.trim());
      const url = `https://open.spotify.com/search/${manual}`;
      toast.error(`No Spotify match for "${t.title || "this track"}". Search Spotify manually →`, {
        id: "deck-unplayable",
        duration: 8000,
        action: { label: "Open Spotify", onClick: () => window.open(url, "_blank", "noopener") },
      });
    }
  };

  // ─── SetList Lock ──────────────────────────────────────────────────
  // Queue state + auto-advance / auto-hydrate machinery lives in
  // /lib/useSetlistLock.js so this page stays focussed on presentation.
  // Declared AFTER smartLoad so the hook's captured reference isn't a
  // temporal-dead-zone violation.
  const {
    setlist,
    setlistLock,
    setSetlistLock,
    enqueueTrack,
    dequeueTrack,
    clearSetlist,
    moveSetlistItem,
  } = useSetlistLock({
    slug,
    deckA, deckB,
    setDeckA, setDeckB,
    setPlayA, setPlayB,
    smartLoad,
    sweepCompletedAt,
    sweepFromRef,
  });

  if (!station) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Loading…</div>;
  if (station.error) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Station not found.</div>;

  return (
    <div data-testid="radio-host-studio" className="h-screen w-screen overflow-hidden bg-[#040609] text-white flex flex-col">
      <RadioHostToolbar
        station={station}
        navigate={navigate}
        live={live}
        goLive={goLive}
        endShow={endShow}
        onOpenHistory={() => setHistoryOpen(true)}
        autoRecord={autoRecord}
        toggleAutoRecord={toggleAutoRecord}
        smartMix={smartMix}
        setSmartMix={setSmartMix}
      />

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">

      {/* Broadcast bar — top so hosts always see their listener URL */}
      <BroadcastPanel slug={slug} stationName={station.name} live={live} deckA={deckA} deckB={deckB} xfader={xfader} onMic={onMic} recorder={recorder} />

      {/* Decks + waveforms */}
      <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px_minmax(0,1fr)] gap-3 p-3 bg-[#04070B]">
        <div className="min-w-0">
          <SeratoDeck
            side="A" track={deckA} audioRef={audioA}
            playing={playA}
            onTogglePlay={() => setPlayA((v) => !v)}
            onCue={() => audioA.current && (audioA.current.currentTime = 0)}
            onClear={() => { setPlayA(false); setDeckA(null); }}
            accent="sky"
            bpm={effectiveBpmA} bpmAnalyzing={bpmA.analyzing}
            storageKey={slug}
            onChannelChange={handleChannelChange}
            onScratchingChange={handleScratchingChange}
            peerTempo={chanB.tempo} peerBpm={effectiveBpmB} peerPlaying={playB} peerLoaded={!!deckB}
            cueEnabled={cueA} onToggleCue={() => setCueA((v) => !v)}
          />
        </div>
        <div className="min-w-0 space-y-3">
          <WaveformStrip
            audioRefA={audioA} audioRefB={audioB}
            colorA="#38bdf8" colorB="#fb7185"
            playingA={playA} playingB={playB}
            tempoA={chanA.tempo} tempoB={chanB.tempo}
            bpmA={effectiveBpmA || 120} bpmB={effectiveBpmB || 120}
          />
          {/* FX unit promoted up — sits directly under the waveform so the
              host can grab Echo/Filter/Flanger without leaving the deck row. */}
          <FxPanel
            fxType={fxType} setFxType={setFxType}
            fxAmount={fxAmount} setFxAmount={setFxAmount}
            fxSendA={fxSendA} setFxSendA={setFxSendA}
            fxSendB={fxSendB} setFxSendB={setFxSendB}
          />
        </div>
        <div className="min-w-0">
          <SeratoDeck
            side="B" track={deckB} audioRef={audioB}
            playing={playB}
            onTogglePlay={() => setPlayB((v) => !v)}
            onCue={() => audioB.current && (audioB.current.currentTime = 0)}
            onClear={() => { setPlayB(false); setDeckB(null); }}
            accent="rose"
            bpm={effectiveBpmB} bpmAnalyzing={bpmB.analyzing}
            storageKey={slug}
            onChannelChange={handleChannelChange}
            onScratchingChange={handleScratchingChange}
            peerTempo={chanA.tempo} peerBpm={effectiveBpmA} peerPlaying={playA} peerLoaded={!!deckA}
            cueEnabled={cueB} onToggleCue={() => setCueB((v) => !v)}
          />
        </div>
      </section>

      {/* Hidden audio elements */}
      {deckA && canDirectPlay(deckA) && <audio data-testid="serato-deck-a-audio" ref={audioA} key={`A-${deckA.id}`} src={deckA.source_url} preload="auto" crossOrigin="anonymous" />}
      {deckB && canDirectPlay(deckB) && <audio data-testid="serato-deck-b-audio" ref={audioB} key={`B-${deckB.id}`} src={deckB.source_url} preload="auto" crossOrigin="anonymous" />}
      <audio data-testid="dj-voice-drop-audio" ref={dropAudioRef} onEnded={onDropEnded} preload="auto" />

      {/* Master bar — Music vol is the centerpiece; other controls flank it
          on both sides (Master vol + Mic on the left, Crossfader + FX FX on
          the right) so hosts get one focal "now playing loud" knob. */}
      <section className="px-3 pb-2 bg-[#04070B]">
        <div className="rounded-lg bg-gradient-to-b from-[#0A0D14] to-[#070A10] border border-white/10 p-3 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(220px,300px)_minmax(0,1fr)] gap-3 items-center">
          {/* LEFT — Master vol + Mic toggle */}
          <div className="flex items-center gap-3 justify-end min-w-0 flex-wrap">
            <div className="flex flex-col items-stretch gap-1.5 min-w-[160px]">
              <button
                data-testid="radio-host-mic-toggle"
                onClick={() => setOnMic((v) => !v)}
                aria-pressed={onMic}
                className={`inline-flex items-center justify-center gap-2 font-bold text-sm py-2.5 px-4 rounded border transition ${
                  onMic
                    ? "bg-red-500/20 text-red-100 border-red-500/50 shadow-[0_0_18px_rgba(239,68,68,0.4)]"
                    : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                }`}
              >
                {onMic ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                {onMic ? (micMusicMode ? "Music IN" : "Mic ON AIR") : (micMusicMode ? "Music input off" : "Tap to talk")}
              </button>
              {/* Mic routing popover — bundles Music-mode pill, FX send pill,
                  and Mic gain slider behind a single trigger so the master
                  bar stays single-row on tablets. */}
              <MicRoutingPopover
                micMusicMode={micMusicMode} setMicMusicMode={setMicMusicMode}
                micFxSend={micFxSend} setMicFxSend={setMicFxSend}
                micGain={micGain} setMicGain={setMicGain}
              />
            </div>
            <MicInputSelector value={micDeviceId} onChange={setMicDeviceId} />
            <div className="w-[140px] shrink-0">
              <VolFader label="Master" testid="radio-host-master-vol" value={masterVol} onChange={setMasterVol} />
            </div>
          </div>

          {/* CENTER — Music (focal control) + scratch monitor knob */}
          <div className="flex flex-col items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-b from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-400/30">
            <div className="text-[9px] uppercase tracking-[0.3em] font-black text-amber-200/80">Music</div>
            <VolFader testid="radio-host-music-vol" value={musicVol} onChange={setMusicVol} hideLabel />
            {/* Scratch monitor — slider for how loud platter scratches bump
                over the live mix. Lit up in cyan while a deck is being
                scratched so you see it kick. */}
            <div className="w-full pt-1 border-t border-amber-400/20">
              <div className="flex items-center justify-between text-[9px] uppercase tracking-wider font-black mb-1">
                <span className={`${(scratchA || scratchB) ? "text-cyan-200 animate-pulse" : "text-white/45"}`}>Scratch monitor</span>
                <span className="text-white/60 tabular-nums">{Math.round(scratchBoost * 100)}%</span>
              </div>
              <input
                data-testid="radio-host-scratch-monitor"
                type="range" min="0" max="1" step="0.05"
                value={scratchBoost}
                onChange={(e) => setScratchBoost(parseFloat(e.target.value))}
                onDoubleClick={() => setScratchBoost(0.45)}
                title="Double-click to reset to 45%"
                className={`w-full h-1.5 ${(scratchA || scratchB) ? "accent-cyan-400" : "accent-tv-accent"}`}
              />
            </div>
          </div>

          {/* RIGHT — Crossfader + FX selector */}
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            <div className="flex-1 min-w-[200px] px-1">
              <div className="flex items-center justify-between text-[9px] uppercase tracking-wider font-black text-white/50 mb-1">
                <span className="text-sky-300">A</span>
                <span>Crossfader</span>
                <span className="text-rose-300">B</span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  data-testid="radio-host-xfader-kill-a"
                  onClick={() => setXfader(0)}
                  title="Slam full Deck A (battle-DJ kill switch)"
                  className="shrink-0 bg-sky-500/20 hover:bg-sky-500 hover:text-black text-sky-200 border border-sky-500/40 font-black text-[9px] px-1.5 py-0.5 rounded"
                >◀ A</button>
                <input
                  data-testid="radio-host-xfader"
                  type="range" min="0" max="1" step="0.01"
                  value={xfader}
                  onChange={(e) => setXfader(parseFloat(e.target.value))}
                  onDoubleClick={() => setXfader(0.5)}
                  title="Double-click to center"
                  className="flex-1 accent-tv-accent h-2"
                />
                <button
                  data-testid="radio-host-xfader-kill-b"
                  onClick={() => setXfader(1)}
                  title="Slam full Deck B (battle-DJ kill switch)"
                  className="shrink-0 bg-rose-500/20 hover:bg-rose-500 hover:text-black text-rose-200 border border-rose-500/40 font-black text-[9px] px-1.5 py-0.5 rounded"
                >B ▶</button>
              </div>
              {/* Sweep-mix macro — animated filter sweep + crossfade in one
                  tap. Tap again to abort mid-sweep. */}
              <button
                data-testid="radio-host-sweep-mix"
                onClick={sweepToNext}
                title={sweeping ? "Cancel sweep" : `Sweep into Deck ${xfader < 0.5 ? "B" : "A"} — animated filter + crossfade, no master-bus touch`}
                className={`mt-1 w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-wider py-1.5 rounded border transition ${
                  sweeping
                    ? "bg-violet-500/30 text-violet-100 border-violet-400/60 animate-pulse"
                    : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white"
                }`}
              >
                {sweeping ? "Sweeping…" : `Sweep → ${xfader < 0.5 ? "B" : "A"}`}
              </button>
              {/* Auto-mix toggle — armed = the dominant deck auto-sweeps to
                  the peer 8 bars before its track ends. Solo DJs leave this
                  on so they don't have to babysit the deck. */}
              <button
                data-testid="radio-host-automix"
                onClick={() => setAutoMix((v) => !v)}
                aria-pressed={autoMix}
                title={autoMix ? "Auto-mix armed — will sweep 8 bars before the dominant track ends. Click to disarm." : "Arm auto-mix — the studio will sweep into the peer deck 8 bars before the current track ends."}
                className={`w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-wider py-1.5 rounded border transition ${
                  autoMix
                    ? "bg-emerald-500/30 text-emerald-100 border-emerald-400/60"
                    : "bg-white/5 text-white/65 border-white/10 hover:bg-white/10"
                }`}
              >
                {autoMix ? "AUTO-MIX • ARMED" : "Auto-mix"}
              </button>
            </div>
            <select
              data-testid="radio-host-fx"
              value={fx}
              onChange={(e) => setFx(e.target.value)}
              className="bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-xs focus:border-tv-accent outline-none max-w-[140px]"
              title="Voice announcer FX"
            >
              {(ANNOUNCER_PRESETS || [{ key: "off", label: "Off" }]).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Music search promoted — TrackBrowser sits directly under the
          master / music bar, with the rest of the host functions flanking it
          on the left and right so the "find a track + load it + then mix"
          loop happens in one row of the screen. */}
      <section className="px-3 pb-2 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] gap-3 bg-[#04070B] items-start">
        {/* LEFT functions column */}
        <div className="space-y-3 min-w-0">
          <MidiPanel midi={midi} />
          <CallInPanel slug={slug} token={callinToken} callers={callin.callers} onOpen={openCallin} onClose={closeCallin} onKick={callin.kick} />
          <HostPlaylistsPanel
            slug={slug}
            tracks={tracks}
            onLoadTrack={(t, deck) => deck === "B"
              ? smartLoad(t, setDeckB, setPlayB)
              : smartLoad(t, setDeckA, setPlayA)
            }
          />
          <AnnouncementsPanel defaultOpen meta={{
            show_name: `${station?.name || "Show"} · Live Mix`,
            host_name: user?.display_name || user?.username,
            station: station?.name,
            listen_url: `${window.location.origin}/radio/station/${slug}`,
          }} />
          <RequestsPanel
            slug={slug}
            onPlayRequest={(r) => {
              // Surface the requested track in a toast so the host can
              // load it manually — we don't auto-load because the request
              // string may not match a track in the library exactly.
              toast.info(`▶ ${r.track_title}${r.artist ? ` · ${r.artist}` : ""} — search the library to load it.`, {
                id: "request-play",
                duration: 6000,
              });
            }}
          />
        </div>

        {/* CENTER — Music search */}
        <div className="min-w-0">
          <SetlistPanel
            setlist={setlist}
            setlistLock={setlistLock}
            onToggleLock={() => setSetlistLock((v) => !v)}
            onMove={moveSetlistItem}
            onRemove={dequeueTrack}
            onClear={clearSetlist}
          />
          <VoiceDropsPanel onPlayDrop={playDrop} />
          <TrackBrowser
            slug={slug}
            tracks={tracks}
            onReload={loadAll}
            onLoadA={(t) => smartLoad(t, setDeckA, setPlayA)}
            onLoadB={(t) => smartLoad(t, setDeckB, setPlayB)}
            onEnqueue={enqueueTrack}
            spotifyAuth={spotifyAuth}
            onLoadSpotifyA={(sp) => sendSpotifyToDeck("A", sp)}
            onLoadSpotifyB={(sp) => sendSpotifyToDeck("B", sp)}
          />
        </div>

        {/* RIGHT functions column */}
        <div className="space-y-3 min-w-0">
          <RecurringTemplatesPanel slug={slug} currentSetlist={setlist} />
          <SponsorDropsPanel onPlayDrop={playDrop} />
          <AdQueuePanel slug={slug} tracks={tracks} />
          <SpotifyPanel
            auth={spotifyAuth}
            player={spotifyPlayer}
            onSendToDeck={sendSpotifyToDeck}
          />
          <RestreamPanel
            getBroadcastStream={canvasBroadcast.getStream}
            meta={{
              show_name: `${station?.name || "Show"} · Live Mix`,
              host_name: user?.display_name || user?.username,
              station: station?.name,
              listen_url: `${window.location.origin}/radio/station/${slug}`,
            }}
            title="Multistream to socials"
          />
          <HostAdSalesPanel slug={slug} tracks={tracks} />
        </div>
      </section>

      {/* Episodes recap — full width, lives below the search row */}
      <section className="px-3 pb-2 bg-[#04070B]">
        <EpisodesPanel slug={slug} recorder={recorder} tracklistRef={tracklistRef} />
      </section>

      </div>
      {historyOpen && (
        <RadioShowHistoryModal
          stationSlug={slug}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}

function VolFader({ label, testid, value, onChange, hideLabel = false }) {
  return (
    <div className="w-full">
      {!hideLabel && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-black text-white/50 mb-1">
          <span>{label}</span>
          <span className="tabular-nums">{Math.round(value * 100)}%</span>
        </div>
      )}
      <input
        data-testid={testid}
        type="range" min="0" max="1" step="0.02"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-tv-accent"
      />
      {hideLabel && (
        <div className="text-center text-amber-100 text-xs font-black tabular-nums mt-0.5">
          {Math.round(value * 100)}%
        </div>
      )}
    </div>
  );
}
